import { memo } from 'react';

import { WorkspaceMemberType } from '@air/api/types';
import { DropdownMenuSeparator } from '@air/primitive-dropdown-menu';

import { CreditUsageBreakdownGiftCreditsRow } from '~/components/CreditUsageMeter/CreditUsageBreakdownGiftCreditsRow';
import { CreditUsageMeterBar } from '~/components/CreditUsageMeter/CreditUsageMeterBar';
import { CreditUsageMeterCount } from '~/components/CreditUsageMeter/CreditUsageMeterCount';
import { CreditUsageMeterLegend } from '~/components/CreditUsageMeter/CreditUsageMeterLegend';
import { CreditUsageMeterResetDate } from '~/components/CreditUsageMeter/CreditUsageMeterResetDate';
import { CreditUsageOverageLabel } from '~/components/CreditUsageMeter/CreditUsageOverageLabel';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { getGiftCreditsBalance } from '~/utils/creditUtils';
import { getAllowCreditOverage } from '~/utils/getAllowCreditOverage';

export const WorkspaceHeaderInformationDropdownCreditUsage = memo(() => {
  const { data: creditStats } = useCreditStats();
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();
  const { data: subscription } = useSubscriptionContext();

  if (!creditStats?.allocated || !currentWorkspaceMember || currentWorkspaceMember.type === WorkspaceMemberType.guest) {
    return null;
  }

  const giftCreditsBalance = getGiftCreditsBalance({
    pricingModel: subscription?.pricingModel,
    statsGiftCreditsBalance: creditStats.giftCreditsBalance,
  });
  const allowCreditOverage = getAllowCreditOverage(creditStats);

  return (
    <>
      <div className="flex flex-col gap-2 px-3 py-2" data-testid="WORKSPACE_HEADER_CREDIT_USAGE">
        <p className="font-semibold ads:text-metadata text-10 text-gray-10 uppercase tracking-wider">
          Workspace Credit Usage
        </p>
        <CreditUsageMeterCount
          allocated={creditStats.allocated}
          allowCreditOverage={allowCreditOverage}
          giftCreditsBalance={giftCreditsBalance}
          used={creditStats.used}
        />
        <CreditUsageOverageLabel allowCreditOverage={allowCreditOverage} />
        <div className="flex flex-col gap-1">
          <CreditUsageMeterBar
            allocated={creditStats.allocated}
            used={creditStats.used}
            giftCreditsBalance={giftCreditsBalance}
            className="ads:bg-input bg-gray-4"
          />
          {subscription?.isCreditModel && (
            <CreditUsageMeterLegend used={creditStats.used} giftCreditsBalance={giftCreditsBalance} />
          )}
          {subscription?.pricingModel === 'fixed' && (
            <CreditUsageBreakdownGiftCreditsRow giftCreditsBalance={giftCreditsBalance} />
          )}
        </div>
        <CreditUsageMeterResetDate nextCycleStartAt={creditStats.nextCycleStartAt} />
      </div>
      <DropdownMenuSeparator />
    </>
  );
});

WorkspaceHeaderInformationDropdownCreditUsage.displayName = 'WorkspaceHeaderInformationDropdownCreditUsage';
